import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Lock,
  GraduationCap,
  ArrowRight,
  BadgeCheck,
  Sparkles,
} from "lucide-react";

const PILLARS = [
  { icon: ShieldCheck, title: "Rigorously Vetted", body: "Every caregiver clears background checks, TrustLine, and a multi-stage interview." },
  { icon: GraduationCap, title: "Continuously Trained", body: "Annual CPR + First Aid, plus proprietary premium-care and safeguarding modules." },
  { icon: BadgeCheck, title: "Elite Environment", body: "Work with high-profile families across weddings, hotels, private travel and executive events." },
];

const Careers = () => {
  return (
    <main data-testid="careers-page" className="min-h-screen bg-background text-foreground font-body">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display text-lg">C</span>
          <span className="font-serif-display text-xl md:text-2xl">Care Anywhere</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/careers/login"
            data-testid="careers-login-link"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Applicant sign-in
          </Link>
          <Button asChild data-testid="careers-apply-cta" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6">
            <Link to="/careers/apply">Apply now <ArrowRight size={14} className="ml-1.5" /></Link>
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-secondary/60 blur-3xl opacity-70 pointer-events-none" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 lg:px-16 text-left">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 backdrop-blur px-4 py-1.5 text-xs tracking-widest uppercase text-muted-foreground">
            <Sparkles size={13} className="text-primary" /> Now hiring elite caregivers
          </div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Bring your craft to the families who won't <span className="italic text-primary">compromise</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-muted-foreground text-base md:text-lg leading-relaxed">
            Join a discreet, meticulously-vetted team caring for children at weddings, five-star hotels, private travel, and executive events. Premium pay, exceptional environments, and a training program to match.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button asChild data-testid="careers-apply-primary" className="rounded-full h-12 px-7 bg-primary hover:bg-primary/90 text-primary-foreground text-sm tracking-wide">
              <Link to="/careers/apply">Start my application <ArrowRight size={15} className="ml-1.5" /></Link>
            </Button>
            <Button asChild variant="outline" data-testid="careers-existing-app" className="rounded-full h-12 px-7 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary">
              <Link to="/careers/login">Continue my application</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 pb-24 grid grid-cols-1 md:grid-cols-3 gap-8">
        {PILLARS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-border/70 bg-card p-8">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Icon size={22} className="text-primary" />
            </div>
            <h3 className="font-serif-display text-2xl">{title}</h3>
            <p className="mt-3 text-muted-foreground text-sm leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      {/* Compliance banner */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="rounded-2xl border border-border/70 bg-muted/40 p-6 md:p-8 flex items-start gap-4">
          <Lock size={22} className="text-primary shrink-0 mt-1" />
          <div className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Your data, protected.</span>{" "}
            Documents you upload (Passport, Driver's ID, Livescan, CPR cert) are encrypted at rest with AES-256 and only accessible by our HR team for employment vetting and background checks in compliance with applicable state labor laws. Your Social Security Number is masked in the browser and encrypted the moment you submit — we never store it in plain text.
          </div>
        </div>
      </section>
    </main>
  );
};

export default Careers;
